/**
 * DeviceFilters — shared filter row used by Dashboard, OpsDevices, and
 * FleetMap. Wraps a search box + group/tenant/online dropdowns into a
 * single wrap-friendly flex bar. The match summary (or any other "end
 * slot" content the consumer wants) lives at the right edge.
 *
 * Tenant dropdown is conditional on `showTenant` — Dashboard hides it
 * for tenant-admins (whose RLS scope already constrains them to one
 * tenant), Ops shows it always since it's super-admin-gated.
 */
import {
  ARTOMEUI_ENV_GROUPS,
  ENV_GROUP_LABEL,
  type EnvGroup,
} from '../api/hooks';

export type OnlineFilter = 'all' | 'online' | 'offline';
export type GroupFilter = 'all' | EnvGroup;

interface DeviceFiltersProps {
  search: string;
  setSearch: (v: string) => void;
  groupFilter: GroupFilter;
  setGroupFilter: (v: GroupFilter) => void;
  tenantFilter: string;
  setTenantFilter: (v: string) => void;
  onlineFilter: OnlineFilter;
  setOnlineFilter: (v: OnlineFilter) => void;
  /** Available tenants. Ignored when `showTenant` is false. */
  tenants: string[];
  /** Render the tenant dropdown. Gate on `me.scope === 'all'`. */
  showTenant: boolean;
  /**
   * Right-aligned slot — typically the "X of N match" counter, but the
   * Map view also packs the Fit view / Clear filters buttons here so
   * they sit on the same row.
   */
  endSlot?: React.ReactNode;
  /** Override the search input placeholder. Defaults to a generic prompt. */
  searchPlaceholder?: string;
  /**
   * Optional manual-refresh handler — typically `useDevices().refetch`.
   * When provided, a ↻ chip renders before the endSlot. Useful when the
   * operator is waiting on a known imminent change and doesn't want to
   * wait for the 30 s auto-refresh.
   */
  refresh?: () => void;
  /**
   * Whether a fetch is currently in flight. Drives the spinner animation
   * and disables the button so rapid double-clicks don't queue duplicate
   * fetches. Pair with React Query's `isFetching`, not `isLoading`, so
   * background polls also spin the icon.
   */
  isFetching?: boolean;
}

const SELECT_CLASS =
  'px-3 py-1.5 text-sm border border-border rounded-md bg-white';

export function DeviceFilters({
  search,
  setSearch,
  groupFilter,
  setGroupFilter,
  tenantFilter,
  setTenantFilter,
  onlineFilter,
  setOnlineFilter,
  tenants,
  showTenant,
  endSlot,
  searchPlaceholder = 'Search thing, name, tenant, serial, FW…',
  refresh,
  isFetching = false,
}: DeviceFiltersProps) {
  return (
    <div className="flex items-center gap-3 mb-2 flex-wrap">
      <input
        type="search"
        placeholder={searchPlaceholder}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="px-3 py-1.5 text-sm border border-border rounded-md w-72"
      />
      <select
        value={groupFilter}
        onChange={(e) => setGroupFilter(e.target.value as GroupFilter)}
        className={SELECT_CLASS}
        aria-label="Filter by env group"
      >
        <option value="all">All groups</option>
        {ARTOMEUI_ENV_GROUPS.map((g) => (
          <option key={g} value={g}>
            {ENV_GROUP_LABEL[g]}
          </option>
        ))}
      </select>
      {showTenant && (
        <select
          value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)}
          className={SELECT_CLASS}
          aria-label="Filter by tenant"
        >
          <option value="all">All tenants</option>
          {tenants.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      )}
      <select
        value={onlineFilter}
        onChange={(e) => setOnlineFilter(e.target.value as OnlineFilter)}
        className={SELECT_CLASS}
        aria-label="Filter by online status"
      >
        <option value="all">Online + offline</option>
        <option value="online">Online only</option>
        <option value="offline">Offline only</option>
      </select>
      {refresh && (
        <button
          type="button"
          onClick={refresh}
          disabled={isFetching}
          title={
            isFetching
              ? 'Refreshing…'
              : 'Refresh now (auto-refreshes every 30 s)'
          }
          aria-label="Refresh devices"
          className="px-2 py-1.5 rounded-md text-sm border border-border bg-white text-muted hover:text-fg disabled:opacity-50 disabled:cursor-not-allowed leading-none"
        >
          <span
            className={`inline-block ${isFetching ? 'animate-spin' : ''}`}
            aria-hidden="true"
          >
            ↻
          </span>
        </button>
      )}
      {endSlot}
    </div>
  );
}

/**
 * Helper that returns true when any of the four filter dimensions is set
 * to something other than its default. Useful for "Clear filters" buttons
 * and conditional match-summary rendering.
 */
export function hasActiveFilters(
  search: string,
  groupFilter: GroupFilter,
  tenantFilter: string,
  onlineFilter: OnlineFilter,
): boolean {
  return (
    search.length > 0 ||
    groupFilter !== 'all' ||
    tenantFilter !== 'all' ||
    onlineFilter !== 'all'
  );
}
